import { createMutationHook } from "@accurx/api/QueryClient";
import {
    ContactFieldError,
    ExternalEmailContact,
    GetContactWithMetadataResponse,
    UpdateContactRequest,
    UpdateContactResponse,
} from "@accurx/api/clinician-messaging";
import { HttpError, httpClient2 } from "@accurx/shared";
import { ContactFormFieldErrors } from "domains/directory/schemas/contact-form/ContactFormSchema";
import { z } from "zod";

import { ContactDetailsQueryKey } from "../queries/useContactDetailsQuery";
import { DirectorySearchQueryKey } from "../queries/useDirectorySearchQuery";

export const UpdateContactKey = "UpdateContact";

type UpdateContactArgs = {
    workspaceId: number;
    contactId: string;
} & UpdateContactRequest;

export const useUpdateContact = createMutationHook<
    UpdateContactArgs,
    UpdateContactResponse
>(({ queryClient }) => {
    return {
        mutationKey: [UpdateContactKey],
        mutationFn: async ({ workspaceId, contactId, ...payload }) => {
            try {
                return await httpClient2
                    .put({
                        path: "/api/clinicianmessaging/directory/:workspaceId/contacts/:contactId",
                        params: {
                            workspaceId,
                            contactId,
                        },
                        json: payload,
                    })
                    .json<UpdateContactResponse>();
            } catch (e) {
                if (e instanceof HttpError && e.statusCode === 400) {
                    const data = ContactUpdateErrorResponseSchema.parse(
                        await e.response.json(),
                    );

                    throw new ContactUpdateError(
                        "Could not update contact",
                        data,
                    );
                }
                throw e;
            }
        },
        onSuccess: (data, { workspaceId, contactId }) => {
            queryClient.setQueriesData<ExternalEmailContact[]>(
                [DirectorySearchQueryKey],
                (currentData) => {
                    if (!currentData) {
                        return;
                    }
                    return currentData.map((contact) => {
                        if (contact.id === data.contact.id) {
                            return data.contact;
                        }
                        return contact;
                    });
                },
            );

            queryClient.setQueryData<GetContactWithMetadataResponse>(
                [ContactDetailsQueryKey, { workspaceId, contactId }],
                (currentData) => ({
                    ...currentData,
                    contact: data.contact,
                }),
            );
        },
    };
});

export class ContactUpdateError extends Error {
    fieldErrors: ContactFormFieldErrors;

    constructor(message: string, fieldErrors: ContactFormFieldErrors) {
        super(message);
        this.fieldErrors = fieldErrors;
    }
}

const mapFieldError = (
    fieldName: string,
    error: ContactFieldError | null,
): string[] | undefined => {
    switch (error) {
        case ContactFieldError.CannotBeNull:
            return [`${fieldName} is required`];
        case ContactFieldError.Invalid:
            return [`${fieldName} is invalid`];
        case ContactFieldError.NotSecure:
            return [`${fieldName} is not secure`];
        default:
            return undefined;
    }
};

const ContactUpdateErrorResponseSchema = z
    .object({
        displayName: z.nativeEnum(ContactFieldError).nullable(),
        notes: z.nativeEnum(ContactFieldError).nullable(),
        phoneNumber: z.nativeEnum(ContactFieldError).nullable(),
        websiteUrl: z.nativeEnum(ContactFieldError).nullable(),
        serviceId: z.nativeEnum(ContactFieldError).nullable(),
        institutionId: z.nativeEnum(ContactFieldError).nullable(),
    })
    .transform(
        (data): ContactFormFieldErrors => ({
            displayName: mapFieldError("Display name", data.displayName),
            notes: mapFieldError("Notes", data.notes),
            phoneNumber: mapFieldError("Phone number", data.phoneNumber),
            websiteUrl: mapFieldError("Website", data.websiteUrl),
            serviceId: mapFieldError("Service", data.serviceId),
            institutionId: mapFieldError("Institution", data.institutionId),
        }),
    );
